/**
 * PEDIT-TREE — state vocabulary + status chip for the project entity tree.
 *
 * The tree spans FOUR domains' state enums (contract, contract-position,
 * inspection-instruction, data-acquisition). Each already has its own status
 * chip elsewhere — `ContractStatusChip` (apps/web), `ContractPositionStatusChip`
 * (apps/web), `InspectionInstructionStatusChip` (apps/web) and
 * `DataAcquisitionStatusChip` (apps/lyse). Rather than reach across the
 * app-package boundary into `apps/lyse` for the DA chip (it isn't exported from
 * the lyse package), this module re-expresses the SAME locked colour bands +
 * labels in ONE place so the tree renders every kind's chip self-contained,
 * using the matching colour vocabulary from `.vibe/_STATE_MACHINE`.
 *
 * The per-kind state → band maps are still copied from the shipped per-domain
 * chips, but the band → FILL step no longer is: every chip in the app now goes
 * through the one shared `statusChipStyle` recipe, so a tree chip is pixel-equal
 * to the chip on the entity's own page by construction rather than by diligence.
 *
 * The old `violet` band is GONE. D20 reserves `--brand-violet` for the second
 * chart series in product only and D81 bars it from status permanently, so the
 * one state that used it — the data acquisition's `EVALUATION_DONE` — moves to
 * ATTENTION: the evaluation is finished and a human must now lock it, which is
 * precisely what "needs a human" means. `PROCESSING` moves off attention to
 * INFO so the two do not collide, and info is the truer slot for it anyway.
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import type { EntityKind } from "./entityTreeModel.js";

type ColorBand = "white" | "yellow" | "blue" | "green" | "red";

/** Band → D81 slot. The band is the domain vocabulary `data-color` publishes;
 *  the slot is the MEANING that carries the colour. */
const SLOT_BY_BAND: Record<ColorBand, StatusSlot> = {
  white: "neutral",
  yellow: "attention",
  blue: "info",
  green: "success",
  red: "critical",
};

/* ── per-kind state → colour band (verbatim from the shipped chips) ──── */

const CONTRACT_BAND: Record<string, ColorBand> = {
  CREATED: "white",
  SCHEDULED: "white",
  EXECUTION: "yellow",
  ACCOUNTING: "yellow",
  PAYMENT: "yellow",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

const POSITION_BAND: Record<string, ColorBand> = {
  CREATED: "white",
  SCHEDULED: "white",
  EXECUTION: "yellow",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

const II_BAND: Record<string, ColorBand> = {
  CREATED: "white",
  SCHEDULED: "white",
  ONSITE_EXECUTION: "yellow",
  DATA_TRANSFER: "yellow",
  DATA_PROCESSING: "blue",
  ANALYSIS: "blue",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

// Kept in lockstep with `DA_STATE_COLOR` in apps/lyse's chip: PROCESSING is
// blue/info (it is literally "running") and EVALUATION_DONE is yellow/attention
// (finished, waiting on a human to lock it) now that violet is retired.
const DA_BAND: Record<string, ColorBand> = {
  CREATED: "white",
  PROCESSING: "blue",
  PRE_SCREENING: "blue",
  EXPERT_ANALYSIS: "blue",
  EVALUATION_DONE: "yellow",
  LOCKED: "green",
  ISSUE: "red",
  REJECTED: "red",
};

const BAND_BY_KIND: Partial<Record<EntityKind, Record<string, ColorBand>>> = {
  contract: CONTRACT_BAND,
  contractPosition: POSITION_BAND,
  inspectionInstruction: II_BAND,
  dataAcquisition: DA_BAND,
};

/** Title-case a SCREAMING_SNAKE state into a human label ("ONSITE_EXECUTION"
 *  → "Onsite execution"). Matches the per-domain chips' label vocabulary. */
export function stateLabel(state: string): string {
  return state
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

export function bandForState(kind: EntityKind, state: string): ColorBand {
  return BAND_BY_KIND[kind]?.[state] ?? "white";
}

/** A state chip for any tree node kind — same bands, and now literally the same
 *  recipe, as the per-domain chips. `data-state` / `data-color` are kept for
 *  test assertions. */
export function EntityStateChip({
  kind,
  state,
}: {
  kind: EntityKind;
  state: string;
}): ReactElement {
  const color = bandForState(kind, state);
  return (
    <Chip
      size="small"
      label={stateLabel(state)}
      data-state={state}
      data-color={color}
      sx={(t) => {
        // The hue owns the fill and the keyline; the label stays neutral. Every
        // band now carries a keyline, not just the old near-transparent white.
        const chip = statusChipStyle(t, SLOT_BY_BAND[color]);
        return {
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        };
      }}
    />
  );
}
